import {
  SYS_BREAKPOINT_LG,
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_SM,
  SYS_BREAKPOINT_XL,
} from '@oneaudi/unified-web-common';
import { TextButton } from '@oneaudi/unified-web-components';
import styled, { css } from 'styled-components';

const ueViewportStyles = css`
  .adobe-ue-edit &,
  .adobe-ue-preview & {
    // this variable is provided by the UE integration
    // it defines the initial viewport height in px
    // before the UE started resizing the iframe
    max-height: var(--ue-viewport-height);
  }
`;

export const SkipButton = styled(TextButton)`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;

  &:focus {
    position: static;
    width: auto;
    height: auto;
    margin: 0;
    overflow: visible;
    clip: auto;
    white-space: normal;
  }
`;

export const SkipSliderContainer = styled.div`
  position: absolute;
  left: -500vw;
  padding-inline: var(--page-margin);
  &:focus {
    position: static;
  }
`;

export const Background = styled.div`
  overflow: hidden;
  background-color: ${({ theme }) => theme.global.color.canvas.default};
`;

export const Gallery = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-xl);
  }

  @media (min-width: ${SYS_BREAKPOINT_LG}) and (orientation: landscape) {
    grid-template-columns: 40% 60%;
  }

  @media (min-width: ${SYS_BREAKPOINT_XL}) {
    grid-template-columns: 30% 70%;
  }
`;

export const AssetsCarousel = styled.div`
  order: 1;

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    order: 2;
  }
`;

export const TextCarousel = styled.div`
  order: 2;
  padding: var(--spacing-relative-lg) var(--spacing-relative-xl);
  display: flex;
  flex-direction: column;

  @media (min-width: ${SYS_BREAKPOINT_SM}) {
    padding: var(--spacing-relative-lg) var(--page-margin);
  }

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    padding: var(--spacing-relative-lg) var(--spacing-relative-xl) var(--spacing-relative-lg)
      var(--page-margin);

    order: 1;
    justify-content: flex-end;
  }

  @media (orientation: landscape) {
    ${ueViewportStyles}
  }
`;

export const CarouselSection = styled.div`
  @media (max-width: ${SYS_BREAKPOINT_MD}) and (orientation: landscape) {
    overflow: auto;
  }
  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    height: 60%;
  }
`;

export const StyledPagination = styled.div`
  padding-bottom: var(--spacing-relative-xl);
`;

export const StyledAssetSlide = styled.div<{ active: boolean }>`
  scroll-behavior: auto;

  ${({ active }) => active && `z-index: 1;`}
  position: relative;
  height: 48vh;
  .adobe-ue-edit &,
  .adobe-ue-preview & {
    max-height: calc(0.48 * var(--ue-viewport-height));
  }

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    @media (orientation: portrait) {
      height: 65vh;
      .adobe-ue-edit &,
      .adobe-ue-preview & {
        max-height: calc(0.65 * var(--ue-viewport-height));
      }
    }

    @media (orientation: landscape) {
      height: 100vh;
      ${ueViewportStyles}
    }

    @media (min-width: ${SYS_BREAKPOINT_LG}) and (orientation: landscape) {
      height: 100vh;
      ${ueViewportStyles}
    }
  }
`;

export const StyledTextSlide = styled.div<{ isActive: boolean }>`
  display: ${(props) => (props.isActive ? 'flex' : 'none !important')};

  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  overflow: auto;
  ${ueViewportStyles}
`;

export const StyledCopy = styled.div`
  @media (min-width: ${SYS_BREAKPOINT_LG}) {
    padding-right: var(--spacing-relative-xl);
  }
`;

export const StyledFooter = styled.div`
  padding-top: var(--spacing-relative-md);
`;

export const StyledHeadline = styled.div`
  color: ${({ theme }) => theme.global.color.typography.display.default};
  padding-bottom: var(--spacing-relative-sm);
`;

export const StyledVideo = styled.video`
  z-index: -1;
  position: absolute;
  width: 100%;
  min-height: 100%;
  object-fit: cover;
  overflow: hidden;
  ${ueViewportStyles}
`;

export const VideoContainer = styled.div<{ isActive: boolean }>`
  display: ${(props) => (props.isActive ? 'flex' : 'none !important')};
  height: 100%;
  align-items: center;
  ${ueViewportStyles}
`;

export const VideoControls = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  height: 100%;
  ${ueViewportStyles}
  padding: var(--spacing-relative-lg);

  @media (min-width: ${SYS_BREAKPOINT_MD}) and (orientation: landscape) {
    justify-content: flex-end;
  }
`;

export const VideoButton = styled.button`
  cursor: pointer;
  padding: 0;
  background: transparent;
  border: none;

  @media (min-width: ${SYS_BREAKPOINT_MD}) {
    width: 48px;
    height: 48px;

    &:nth-child(2) {
      margin-left: var(--spacing-relative-lg);
    }
  }
`;
